import { gql } from '@apollo/client';
import { useEffect } from 'react';

import { Server } from '../../apollo/types';
import { useAppDependenciesContext } from '../../state';

type CurrentIntention = {
  shortId: string;
  contentText: string;
  createdAt: string;
};

type GetCurrentIntentionQuery = {
  intentionHistory: {
    currentSessionIntention: CurrentIntention;
  };
};

type GetCurrentIntentionQueryVariables = {
  clientUserShortId: string;
  providerShortId: string;
  appointmentShortId?: string;
};

export const CURRENT_INTENTION_QUERY = gql`
  query GetCurrentIntention($clientUserShortId: String!, $providerShortId: String!, $appointmentShortId: String) {
    intentionHistory(
      clientUserShortId: $clientUserShortId
      providerShortId: $providerShortId
      appointmentShortId: $appointmentShortId
    ) {
      currentSessionIntention {
        createdAt
        shortId
        contentText
      }
    }
  }
`;

export function useGetCurrentIntentionQuery(
  clientUserShortId: string,
  providerShortId: string,
  appointmentShortId?: string,
  { skip, onCompleted }: { skip?: boolean; onCompleted?: (data: GetCurrentIntentionQuery) => void } = {},
) {
  const { useQuery } = useAppDependenciesContext();
  const { data, loading, error } = useQuery<GetCurrentIntentionQuery, GetCurrentIntentionQueryVariables>(
    CURRENT_INTENTION_QUERY,
    {
      variables: {
        clientUserShortId,
        providerShortId,
        appointmentShortId,
      },
      skip: !clientUserShortId || !providerShortId || skip,
      fetchPolicy: 'cache-and-network',
      context: {
        server: Server.MONOLITH,
      },
    },
  );

  useEffect(
    function callOnCompletedCallback() {
      if (data && onCompleted) {
        onCompleted(data);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data],
  );

  return {
    currentIntention: data?.intentionHistory?.currentSessionIntention,
    loading,
    error,
  };
}
